import React, {useCallback, useMemo} from 'react';
import clsx from 'clsx';
import {AttributeType} from '@netx/core-attribute/constants';
import {useAttributes} from '@netx/core-attribute/hooks/useAttributes';
import {DateField} from '@netx/core-components/DateField';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {enumToValues} from '@netx/core-next/utils/enum';
import {i18n} from '@netx/core/i18n';
import {SearchSubTypeLabel} from '../../../../locales';
import type {CommonRuleEditorProps} from '../../../types';
import {DateSubType, DateSystemDefinedTypes, DateType} from '../../constants';
import {dateRuleDefaults} from '../../defaults';
import {DateRuleTypeLabel} from '../../locales';
import {getDateSubTypeLabel} from '../../utils';
import {
	DATE_RULE_EDITOR_CLASS_NAME,
	dateRuleEditorClasses,
	DateRuleEditorClasses,
	getDateRuleEditorUtilityClass,
} from './dateRuleEditorClasses';

export type DateRuleEditorOwnerState = WithClasses<DateRuleEditorClasses, {}>;

const useUtilityClasses = (ownerState: DateRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getDateRuleEditorUtilityClass, classes);
};

const DateRuleEditorRoot = styled('div', {
	name: DATE_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
}));

const DateRuleEditorCriteria = styled('div', {
	name: DATE_RULE_EDITOR_CLASS_NAME,
	slot: 'Criteria',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

const DateRuleEditorValues = styled('div', {
	name: DATE_RULE_EDITOR_CLASS_NAME,
	slot: 'Values',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	marginTop: theme.spacing(2),
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

export type DateRuleEditorProps = WithClasses<
	DateRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: DateType;
		elementSubType1Options: DateType[];
		elementSubType2: DateSubType;
		elementSubType2Options?: DateSubType[];
		elementValue1: string;
		elementValue2: string;
		/** attribute name */
		elementValue3: string;
		onSubType1Change: (subType1: DateType) => void;
		onSubType2Change: (subType2: DateSubType) => void;
		onValue1Change: (value: string) => void;
		onValue2Change: (value: string) => void;
		onValue3Change: (value: string) => void;
	}
>;

const DateRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementSubType2,
	elementSubType2Options,
	elementValue1,
	elementValue2,
	elementValue3,
	validationErrors,
	onSubType1Change,
	onSubType2Change,
	onValue1Change,
	onValue2Change,
	onValue3Change,
}: DateRuleEditorProps) => {
	const ownerState: DateRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const {getByType, data} = useAttributes();

	const _elementSubType2Options = useMemo(
		() => elementSubType2Options || enumToValues(DateSubType),
		[elementSubType2Options],
	);

	const userDates = useMemo(
		() => getByType(AttributeType.DATE, {omitSystem: true}),
		[data],
	);

	const getSubType1OptionLabel = useCallback((option: number | string) => {
		return DateRuleTypeLabel[option as DateType] || option.toString();
	}, []);

	const options: Array<string | number> = useMemo(
		() => [
			...elementSubType1Options.filter(
				(opt) => opt !== DateType.USER_DEFINED_ATTRIBUTE,
			),
			...userDates.map((d) => d.name),
		],
		[elementSubType1Options],
	);

	return (
		<DateRuleEditorRoot className={clsx(classes.root, className)}>
			<DateRuleEditorCriteria>
				<SelectField<number | string, false, true, false>
					data-testid="date-rule-sub-type-1"
					disableClearable
					label={i18n.t('i18n.date')}
					name={'rule-sub-type-1'}
					options={options}
					value={
						DateSystemDefinedTypes.includes(
							elementSubType1 as typeof DateSystemDefinedTypes[number],
						)
							? elementSubType1
							: elementValue3 || dateRuleDefaults.elementSubType1
					}
					getOptionLabel={getSubType1OptionLabel}
					onChange={(event, subType1) => {
						if (
							DateSystemDefinedTypes.includes(
								subType1 as typeof DateSystemDefinedTypes[number],
							)
						) {
							onSubType1Change(subType1 as DateType);
							return;
						}
						onSubType1Change(DateType.USER_DEFINED_ATTRIBUTE);
						onValue3Change(subType1.toString());
					}}
				/>

				<SelectField<DateSubType, false, true, false>
					data-testid="date-rule-sub-type-2"
					disableClearable
					label={SearchSubTypeLabel.MATCH_TYPE}
					name={'rule-sub-type-2'}
					options={_elementSubType2Options}
					value={elementSubType2}
					getOptionLabel={getDateSubTypeLabel}
					onChange={(event, value) => onSubType2Change(value)}
				/>
			</DateRuleEditorCriteria>

			<DateRuleEditorValues>
				{[
					DateSubType.AFTER,
					DateSubType.BEFORE,
					DateSubType.IN_BETWEEN,
				].includes(elementSubType2) ? (
					<DateField
						fullWidth
						data-testid="date-rule-value-1"
						error={validationErrors?.elementValue1?.message}
						label={
							elementSubType2 === DateSubType.AFTER
								? i18n.t('i18n.afterDate')
								: elementSubType2 === DateSubType.BEFORE
								? i18n.t('i18n.beforeDate')
								: i18n.t('i18n.from')
						}
						name="rule-value-1"
						required
						value={elementValue1 || null}
						onChange={(event, value) => onValue1Change(value || '')}
					/>
				) : null}

				{[DateSubType.IN_BETWEEN].includes(elementSubType2) ? (
					<DateField
						fullWidth
						data-testid="date-rule-value-2"
						error={validationErrors?.elementValue2?.message}
						label={i18n.t('i18n.to')}
						name="rule-value2"
						required
						value={elementValue2 || null}
						onChange={(event, value) => onValue2Change(value || '')}
					/>
				) : null}
			</DateRuleEditorValues>
		</DateRuleEditorRoot>
	);
};

export const DateRuleEditorPrimitive = DateRuleEditorInner;

export const DateRuleEditor = DateRuleEditorPrimitive;

export default DateRuleEditor;

export type DateRuleEditorGenericProps = WithClasses<
	DateRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const DateRuleEditorGenericPrimitive = ({
	elementValue1,
	elementValue2,
	elementValue3,
	...props
}: DateRuleEditorGenericProps) => {
	return (
		<DateRuleEditor
			{...props}
			elementValue1={elementValue1.toString()}
			elementValue2={elementValue2.toString()}
			elementValue3={elementValue3.toString()}
		/>
	);
};

export const DateRuleEditorGeneric = DateRuleEditorGenericPrimitive;
